import { useState } from "react";
import { ChevronLeft, ChevronRight, RefreshCw } from "lucide-react";
import { ROLE_NAMES, num } from "@/lib/staff";

export const CARD = "rounded-2xl border border-[#1B2740] bg-[#0E1626]";
export const INPUT = "h-9 rounded-lg border border-[#22304B] bg-[#0B1220] px-3 text-sm text-neutral-100 placeholder:text-neutral-500 outline-none focus:border-sky-400/70 focus:ring-2 focus:ring-sky-400/20 transition-colors [color-scheme:dark]";
const PILL = "inline-flex items-center justify-center gap-2 h-9 px-4 rounded-full text-sm font-medium disabled:opacity-50 disabled:pointer-events-none transition-colors outline-none focus-visible:ring-2 focus-visible:ring-sky-400/70";
export const BTN = `${PILL} bg-sky-400 text-white hover:bg-sky-500`;
export const BTN_GHOST = `${PILL} border border-[#22304B] text-neutral-200 hover:bg-white/[0.05]`;
export const BTN_DANGER = `${PILL} bg-red-500 text-white hover:bg-red-400`;
export const BTN_DANGER_GHOST = `${PILL} border border-red-400/30 text-red-300 hover:bg-red-500/10`;
export const DIALOG = "bg-[#0E1626] border-[#1B2740] text-neutral-100 max-w-md sm:rounded-2xl";

export function PageHead({ title, sub, children }) {
  return (
    <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
      <div className="min-w-0">
        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-white" data-testid="staff-page-title">{title}</h1>
        {sub && <p className="text-sm text-neutral-400 mt-1 max-w-2xl">{sub}</p>}
      </div>
      {children && <div className="flex flex-wrap items-center gap-2">{children}</div>}
    </div>
  );
}

export function Card({ title, action, children, className = "", testid }) {
  return (
    <section className={`${CARD} ${className}`} data-testid={testid}>
      {(title || action) && (
        <div className="flex items-center justify-between gap-3 px-5 pt-4 pb-3">
          <h2 className="text-sm font-medium text-neutral-200">{title}</h2>{action}
        </div>
      )}
      <div className={title || action ? "px-5 pb-5" : "p-5"}>{children}</div>
    </section>
  );
}

export function Stat({ label, value, hint, testid }) {
  return (
    <div className={`${CARD} px-4 py-3.5`}>
      <p className="text-xs text-neutral-500">{label}</p>
      <p className="font-display text-2xl font-semibold text-white mt-1 tabular-nums" data-testid={testid}>{value}</p>
      {hint && <p className="text-[11px] text-neutral-500 mt-0.5">{hint}</p>}
    </div>
  );
}

const TONES = {
  green: "bg-emerald-400/10 text-emerald-300 border-emerald-400/25", red: "bg-red-500/10 text-red-300 border-red-400/25",
  amber: "bg-amber-400/10 text-amber-200 border-amber-300/25", sky: "bg-sky-400/10 text-sky-200 border-sky-400/30",
  neutral: "bg-white/[0.04] text-neutral-300 border-white/10",
};
export const Badge = ({ tone = "neutral", children, testid }) => (
  <span className={`inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium whitespace-nowrap ${TONES[tone]}`} data-testid={testid}>{children}</span>
);
const STATUS = { active: ["green", "Active"], suspended: ["red", "Suspended"], deletion: ["amber", "Deletion scheduled"] };
export const StatusBadge = ({ status, testid }) => <Badge tone={(STATUS[status] || ["neutral"])[0]} testid={testid}>{(STATUS[status] || [0, status])[1]}</Badge>;
export const RoleBadge = ({ role, testid }) => (role ? <Badge tone="sky" testid={testid}>{ROLE_NAMES[role] || role}</Badge> : null);

export function ErrorBox({ error, onRetry, testid = "staff-error" }) {
  return (
    <div className="rounded-xl border border-red-400/25 bg-red-500/[0.06] px-4 py-3 text-sm text-red-200 flex flex-wrap items-center justify-between gap-3" data-testid={testid} role="alert">
      <span>{error}</span>
      {onRetry && <button onClick={onRetry} className={BTN_GHOST} data-testid={`${testid}-retry`}><RefreshCw className="h-3.5 w-3.5" />Try again</button>}
    </div>
  );
}

export const Skeleton = ({ rows = 3 }) => (
  <div className="space-y-2" data-testid="staff-loading">{Array.from({ length: rows }, (_, i) => <div key={i} className="h-9 rounded-lg skeleton" />)}</div>
);

function Body({ columns, rows, rowKey, loading, error, onRetry, empty, onRowClick, testid }) {
  const span = columns.length;
  if (error && !rows) return <tr><td colSpan={span} className="p-4"><ErrorBox error={error} onRetry={onRetry} testid={`${testid}-error`} /></td></tr>;
  if (!rows && loading) return <tr><td colSpan={span} className="p-4"><Skeleton /></td></tr>;
  if (!rows?.length) return <tr><td colSpan={span} className="px-4 py-10 text-center text-neutral-500" data-testid={`${testid}-empty`}>{empty}</td></tr>;
  return rows.map((r) => (
    <tr key={rowKey(r)} data-testid={`${testid}-row-${rowKey(r)}`} onClick={onRowClick ? () => onRowClick(r) : undefined}
      onKeyDown={onRowClick ? (e) => e.key === "Enter" && onRowClick(r) : undefined} tabIndex={onRowClick ? 0 : undefined}
      className={`border-t border-[#162236] ${onRowClick ? "cursor-pointer hover:bg-white/[0.03] focus-visible:bg-white/[0.04] outline-none" : ""}`}>
      {columns.map((c) => <td key={c.key} className={`px-4 py-2.5 align-top ${c.right ? "text-right tabular-nums whitespace-nowrap" : ""} ${c.className || ""}`}>{c.render ? c.render(r) : r[c.key]}</td>)}
    </tr>
  ));
}

export function Table({ columns, rows, rowKey, loading, error, onRetry, empty = "Nothing to show yet.", onRowClick, testid, maxH = "max-h-[65vh]" }) {
  return (
    <div className={`${CARD} overflow-hidden`} data-testid={testid}>
      <div className={`overflow-auto scroll-thin ${maxH}`}>
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#101A2C] text-[11px] uppercase tracking-wider text-neutral-500">
            <tr>{columns.map((c) => <th key={c.key} scope="col" className={`px-4 py-2.5 font-medium whitespace-nowrap ${c.right ? "text-right" : "text-left"}`}>{c.label}</th>)}</tr>
          </thead>
          <tbody className="text-neutral-200">
            <Body columns={columns} rows={rows} rowKey={rowKey} loading={loading} error={error} onRetry={onRetry} empty={empty} onRowClick={onRowClick} testid={testid} />
          </tbody>
        </table>
      </div>
    </div>
  );
}

export function Pager({ page, pageSize, total, onPage, testid }) {
  const pages = Math.max(1, Math.ceil((total || 0) / pageSize));
  const btn = "h-8 w-8 rounded-full border border-[#22304B] flex items-center justify-center hover:bg-white/[0.05] disabled:opacity-40 disabled:pointer-events-none";
  return (
    <div className="flex items-center justify-between gap-3 mt-3 text-xs text-neutral-500" data-testid={testid}>
      <span data-testid={`${testid}-summary`}>{total ? `${num((page - 1) * pageSize + 1)}–${num(Math.min(page * pageSize, total))} of ${num(total)}` : "No results"}</span>
      <div className="flex items-center gap-2">
        <button disabled={page <= 1} onClick={() => onPage(page - 1)} className={btn} aria-label="Previous page" data-testid={`${testid}-prev`}><ChevronLeft className="h-4 w-4" /></button>
        <span>Page {page} of {pages}</span>
        <button disabled={page >= pages} onClick={() => onPage(page + 1)} className={btn} aria-label="Next page" data-testid={`${testid}-next`}><ChevronRight className="h-4 w-4" /></button>
      </div>
    </div>
  );
}

const PRESETS = [["today", "Today"], ["7d", "7 days"], ["30d", "30 days"], ["custom", "Custom"]];

function CustomDates({ value, onChange, testid }) {
  const [from, setFrom] = useState(value.from);
  const [to, setTo] = useState(value.to);
  return (
    <div className="flex flex-wrap items-center gap-2">
      <input type="date" value={from} max={to} onChange={(e) => setFrom(e.target.value)} className={INPUT} aria-label="From date" data-testid={`${testid}-from`} />
      <span className="text-xs text-neutral-500">to</span>
      <input type="date" value={to} min={from} onChange={(e) => setTo(e.target.value)} className={INPUT} aria-label="To date" data-testid={`${testid}-to`} />
      <button onClick={() => onChange({ preset: "custom", from, to })} disabled={!from || !to || from > to} className={BTN_GHOST} data-testid={`${testid}-apply`}>Apply</button>
    </div>
  );
}

export function RangePicker({ value, onChange, testid = "range" }) {
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid={testid}>
      <div className="inline-flex rounded-full border border-[#22304B] p-0.5 bg-[#0B1220]" role="group" aria-label="Date range">
        {PRESETS.map(([k, l]) => (
          <button key={k} onClick={() => onChange({ ...value, preset: k })} aria-pressed={value.preset === k} data-testid={`${testid}-${k}`}
            className={`h-8 px-3 rounded-full text-xs transition-colors ${value.preset === k ? "bg-sky-400 text-white" : "text-neutral-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      {value.preset === "custom" && <CustomDates value={value} onChange={onChange} testid={testid} />}
    </div>
  );
}

export function Select({ value, onChange, options, label, testid }) {
  return (
    <select value={value} onChange={(e) => onChange(e.target.value)} aria-label={label} className={`${INPUT} pr-8`} data-testid={testid}>
      {options.map(([v, l]) => <option key={v} value={v} className="bg-[#0B1220]">{l}</option>)}
    </select>
  );
}

export const Field = ({ label, children }) => (
  <label className="block space-y-1.5"><span className="block text-xs text-neutral-400">{label}</span>{children}</label>
);

export const Row = ({ label, children, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2 border-t border-[#162236] first:border-t-0 text-sm">
    <span className="text-neutral-500 shrink-0">{label}</span><span className="text-right text-neutral-200 min-w-0 break-words" data-testid={testid}>{children}</span>
  </div>
);
